/**
 * Share copy and share targets. Captions stay in English on purpose: they are
 * posted publicly, to an audience that isn't the guide's UI language.
 */

export type CaptionChannel = 'whatsapp' | 'facebook' | 'instagram';

export function buildCaption(channel: CaptionChannel, link: string): string {
  switch (channel) {
    case 'whatsapp':
      return [
        "Hey! I'm part of Trplin, a travel community connecting travellers with local guides. You should join as a guide too!",
        '',
        'Sign up using my referral link:',
        link,
        '',
        "Let's grow together with Trplin!",
      ].join('\n');
    case 'facebook':
      return [
        'Love travel? Turn it into something more.',
        '',
        "I've been guiding travellers with Trplin — a community that connects curious travellers with passionate local guides who know the stories, food and hidden gems of their city.",
        '',
        "If you're a travel guide (or dream of becoming one), join me on Trplin. Connect with travellers, share your local knowledge and grow with a community that loves exploring.",
        '',
        'Apply with my referral link:',
        link,
      ].join('\n');
    case 'instagram':
      return [
        "Travel is more than visiting places. It's about sharing stories, experiences and the hidden gems that make every destination special.",
        '',
        "I'm growing with Trplin, a community that connects travellers with passionate local guides.",
        '',
        'Are you a travel enthusiast? Join me and become part of the journey!',
        '',
        'Join through my referral link:',
        link,
        '',
        '#Trplin #TravelGuide #LocalGuide #TravelCommunity #ExploreWithLocals #TravelIndia #TravelExperiences',
      ].join('\n');
  }
}

export function whatsappShareUrl(link: string): string {
  return `https://wa.me/?text=${encodeURIComponent(buildCaption('whatsapp', link))}`;
}

/** Facebook only accepts the URL; the preview comes from the page's OG tags. */
export function facebookShareUrl(link: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`;
}

export function openShareWindow(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer,width=640,height=720');
}

export function canNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

export function canShareFiles(file: File): boolean {
  try {
    return (
      typeof navigator !== 'undefined' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [file] })
    );
  } catch {
    return false;
  }
}

export type NativeShareOutcome = 'shared' | 'cancelled' | 'unsupported' | 'failed';

export async function nativeShare(data: ShareData): Promise<NativeShareOutcome> {
  if (!canNativeShare()) return 'unsupported';
  try {
    await navigator.share(data);
    return 'shared';
  } catch (error) {
    return (error as { name?: string } | null)?.name === 'AbortError' ? 'cancelled' : 'failed';
  }
}
